import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api/client.js';
import { useApiQuery } from '../../api/useApiQuery.js';
import { Icon } from '../../components/Icon.jsx';
import { Modal } from '../../components/Modal.jsx';

const PAGE_SIZE = 20;

function slugify(value) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function CreateTenantModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [menuLimit, setMenuLimit] = useState('1');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  function handleNameChange(value) {
    setName(value);
    if (!slugTouched) setSlug(slugify(value));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const tenant = await api.post('/api/super-admin/tenants', {
        name: name.trim(),
        slug: slug.trim(),
        menuLimit: Number(menuLimit),
        contactEmail: contactEmail.trim() || undefined,
        currency: currency.trim() || undefined,
      });
      onCreated(tenant);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} labelledBy="create-tenant-title" busy={submitting}>
      <h2 id="create-tenant-title">Create tenant</h2>
      <p className="modal-description">Add a restaurant to your platform. You can invite its team next.</p>

      {error && <p className="field-error">{error}</p>}

      <form onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor="tenant-name">Restaurant name</label>
          <input
            id="tenant-name"
            type="text"
            required
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="tenant-slug">URL slug</label>
          <input
            id="tenant-slug"
            type="text"
            required
            pattern="[a-z0-9]+(-[a-z0-9]+)*"
            title="Lowercase letters, numbers, and hyphens only"
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
          />
          <span className="field-hint">Lowercase letters, numbers, and hyphens only, e.g. "joes-pizza".</span>
        </div>

        <div className="field">
          <label htmlFor="tenant-contact-email">Contact email (optional)</label>
          <input
            id="tenant-contact-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="tenant-currency">Currency code (optional)</label>
          <input
            id="tenant-currency"
            type="text"
            maxLength={3}
            pattern="[A-Za-z]{3}"
            title="3-letter currency code, e.g. INR"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          />
          <span className="field-hint">3-letter code, e.g. INR. Defaults to Indian rupees (₹) if left blank.</span>
        </div>

        <div className="field">
          <label htmlFor="tenant-menu-limit">Menu limit</label>
          <input
            id="tenant-menu-limit"
            type="number"
            min="1"
            max="100"
            step="1"
            required
            value={menuLimit}
            onChange={(e) => setMenuLimit(e.target.value)}
          />
          <span className="field-hint">How many active menus this restaurant can create. Defaults to 1; you can raise it later.</span>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create tenant'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function TenantsListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('pageSize', String(PAGE_SIZE));
    if (isActiveFilter) params.set('isActive', isActiveFilter);
    if (appliedSearch) params.set('search', appliedSearch);
    return params.toString();
  }, [page, appliedSearch, isActiveFilter]);

  const { data, error, loading, reload } = useApiQuery(
    () => api.get(`/api/super-admin/tenants?${queryString}`),
    [queryString],
  );

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  }

  function handleFilterChange(value) {
    setPage(1);
    setIsActiveFilter(value);
  }

  function clearFilters() {
    setSearch('');
    setAppliedSearch('');
    setIsActiveFilter('');
    setPage(1);
  }

  function handleCreated(tenant) {
    setShowCreateModal(false);
    navigate(`/super-admin/tenants/${tenant.id}`);
  }

  const hasFilters = Boolean(appliedSearch) || Boolean(isActiveFilter);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 1;

  return (
    <div className="page tenants-page lp-theme">
      <div className="page-header">
        <div>
          <span className="page-eyebrow">Platform overview</span>
          <h1>Tenants</h1>
          <p>Manage your restaurants and the people behind them.</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <Icon name="plus" size={17} /> Create tenant
        </button>
      </div>

      <section className="tenant-directory" aria-label="Tenant directory" aria-busy={loading}>
        <div className="tenant-directory__heading">
          <h2>All restaurants {data && <span className="count-label">{total}</span>}</h2>
          <span className="field-hint">{hasFilters ? 'Filtered results' : 'Your restaurant directory'}</span>
        </div>
        <form onSubmit={handleSearchSubmit} className="tenant-toolbar">
          <div className="field tenant-toolbar__search">
            <label htmlFor="tenant-search">Search by name</label>
            <div className="search-input">
              <Icon name="search" size={17} />
              <input
                id="tenant-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tenants…"
              />
            </div>
          </div>
          <div className="field tenant-toolbar__status">
            <label htmlFor="tenant-status-filter">Status</label>
            <select
              id="tenant-status-filter"
              value={isActiveFilter}
              onChange={(e) => handleFilterChange(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="tenant-toolbar__actions">
            <button type="submit" className="btn" disabled={loading} aria-busy={loading || undefined}>
              {loading ? 'Searching…' : 'Search'}
            </button>
            {hasFilters && <button type="button" className="btn btn-ghost" onClick={clearFilters}>Clear</button>}
          </div>
        </form>

        {error && <div className="alert alert-error">{error.message}</div>}

        {loading && !data && (
          <div className="empty" role="status">
            <span className="spinner" /> Loading…
          </div>
        )}

        {!loading && data && items.length === 0 && !hasFilters && (
          <div className="empty">
            <span className="empty__icon"><Icon name="restaurant" size={26} /></span>
            <strong>No tenants yet</strong>
            <p>Create your first tenant to get a restaurant started on the platform.</p>
            <button type="button" className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <Icon name="plus" size={16} /> Create tenant
            </button>
          </div>
        )}

        {!loading && data && items.length === 0 && hasFilters && (
          <div className="empty">
            <span className="empty__icon"><Icon name="search" size={26} /></span>
            <strong>No tenants match your filters</strong>
            <p>Try a different search term or status filter.</p>
          </div>
        )}

        {items.length > 0 && (
          <div className="tenant-list">
            <div className="tenant-list__columns" aria-hidden="true"><span>Restaurant</span><span>Menus / limit</span><span>Team</span><span>Status</span><span /></div>
            {items.map((tenant) => (
              <Link
                key={tenant.id}
                to={`/super-admin/tenants/${tenant.id}`}
                className="tenant-row"
              >
                <div className="tenant-row__identity">
                  <span className="tenant-row__avatar" aria-hidden="true">{tenant.name.charAt(0).toUpperCase()}</span>
                  <div><strong>{tenant.name}</strong><span>/{tenant.slug}</span></div>
                </div>
                <span className="tenant-row__menus"><span className="sr-only">Menus: </span>{tenant._count?.menus ?? 0}<span className="text-muted"> / {tenant.menuLimit ?? 1}</span></span>
                <span className="tenant-row__team">{tenant._count?.admins ?? 0} {tenant._count?.admins === 1 ? 'admin' : 'admins'}</span>
                <div className="tenant-row__status">
                  <span className={`badge ${tenant.isActive ? 'badge-success' : 'badge-muted'}`}>
                    {tenant.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <Icon name="chevron" size={16} />
              </Link>
            ))}
          </div>
        )}

        {items.length > 0 && totalPages > 1 && (
          <div className="tenant-pagination">
            <button type="button" className="btn btn-sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
              Previous
            </button>
            <span className="field-hint">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-sm"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>

      {showCreateModal && (
        <CreateTenantModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(tenant) => {
            reload();
            handleCreated(tenant);
          }}
        />
      )}
    </div>
  );
}
